import { useTranslation } from 'react-i18next';
import type { INodeConfig } from '@/types/docflow';

interface ClaudeVisionConfigProps {
  config: INodeConfig;
  onChange: (config: INodeConfig) => void;
}

const CLAUDE_MODELS = [
  { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku (Fast)' },
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Balanced)' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (Best)' },
];

const GEMINI_MODELS = [
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Fast)' },
  { value: 'gemini-2.5-flash-preview-05-20', label: 'Gemini 2.5 Flash (Balanced)' },
  { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro (Best)' },
];

const ClaudeVisionConfig: React.FC<ClaudeVisionConfigProps> = ({ config, onChange }) => {
  const { t } = useTranslation('docflow');

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {t('node.claudeVision')}
      </h3>

      {/* Model selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('config.model')}
        </label>
        <select
          value={config.model || 'claude-3-haiku-20240307'}
          onChange={(e) => onChange({ ...config, model: e.target.value })}
          className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200"
        >
          <optgroup label="Anthropic Claude">
            {CLAUDE_MODELS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Google Gemini">
            {GEMINI_MODELS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* TW Invoice Mode */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.tw_invoice_mode || false}
            onChange={(e) => onChange({ ...config, tw_invoice_mode: e.target.checked, tw_invoice_type: e.target.checked ? config.tw_invoice_type : undefined })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('config.twInvoiceMode')}
          </span>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
          {t('config.twInvoiceModeHint')}
        </p>
      </div>

      {/* TW Invoice Type selector (only when TW Invoice Mode is on) */}
      {config.tw_invoice_mode && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('config.twInvoiceType')}
          </label>
          <select
            value={config.tw_invoice_type || ''}
            onChange={(e) => onChange({ ...config, tw_invoice_type: e.target.value || undefined })}
            className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200"
          >
            <option value="">{t('config.twInvoiceTypeAuto')}</option>
            <option value="電子發票_熱感紙">電子發票_熱感紙</option>
            <option value="電子發票_雲端">電子發票_雲端</option>
            <option value="三聯式">三聯式</option>
            <option value="二聯式">二聯式</option>
            <option value="特種發票">特種發票</option>
          </select>
        </div>
      )}

      {/* Custom prompt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('config.prompt')}
        </label>
        <textarea
          value={config.prompt || ''}
          onChange={(e) => onChange({ ...config, prompt: e.target.value })}
          placeholder={t('config.promptPlaceholder')}
          rows={4}
          className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200 resize-y"
        />
      </div>
    </div>
  );
};

export default ClaudeVisionConfig;
